import {Card, Chip, Text} from '@albert-io/atomic';
import {useDroppable, useDraggable} from '@dnd-kit/core';
import cx from 'classnames';
import React, {useEffect} from 'react';
import {WordBankItem} from '../store';
import {supplementRegexBaseString} from 'client/generic/MarkdownRendererV2/rules/supplementRules';
import {makeParser} from 'generic/MarkdownRendererV2/markdownRendererUtils';
import classNames from 'classnames';

const isSupplement = (value: string) => {
  const regex = new RegExp(`^${supplementRegexBaseString}`);
  return regex.test(value);
};

const getSupplementRenderer = (value: string) => {
  const parser = makeParser();
  const syntaxTree = parser.makeSyntaxTree(value);
  return parser.reactOutput(syntaxTree);
};

const renderStringOrSupplement = (value: string) => {
  if (isSupplement(value)) {
    return getSupplementRenderer(value);
  }
  return value;
};

interface MovableItemProps {
  id: string;
  value: string;
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  disabled?: boolean;
}

export const MovableItem = ({id, value, isActive, onClick, isDragging}: MovableItemProps) => {
  const {attributes, listeners, setNodeRef, transform} = useDraggable({id});

  const style = transform
    ? {
        transform: `translate3d(${transform?.x}px, ${transform?.y}px, 0)`
      }
    : {};

  const valueRenderer = renderStringOrSupplement(value);

  const handleClick = (e: React.MouseEvent) => {
    // Only handle clicks if we're not dragging
    // dnd-kit will handle the drag automatically
    e.stopPropagation();
    onClick(e);
  };

  return (
    <Chip
      as='button'
      color='brand'
      className={cx('z-10 !m-x0 cursor-pointer object-contain', {
        '-rotate-6': isActive,
        'opacity-50': isDragging
      })}
      style={style}
      ref={setNodeRef}
      onClick={handleClick}
      type='button'
      {...attributes}
      {...listeners}
    >
      {valueRenderer}
    </Chip>
  );
};

interface DroppableItemProps {
  id: string;
  placedItem?: WordBankItem;
  isActiveTarget: boolean;
  onClick: () => void;
  onItemClick: (itemId: string, e?: React.MouseEvent) => void;
  activeItemId: string | null;
  isDragging: boolean;
  value?: string;
  disabled?: boolean;
}

export const DroppableItem = ({
  id,
  placedItem,
  isActiveTarget,
  onClick,
  onItemClick,
  activeItemId,
  isDragging,
  value
}: DroppableItemProps) => {
  const {isOver, setNodeRef, node} = useDroppable({id});

  const handleSlotClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const valueRenderer = value ? renderStringOrSupplement(value) : null;

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      if (node.current && node.current.contains(e.target as Node)) {
        e.stopPropagation();
        onClick();
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [onClick, node, activeItemId]);

  return (
    <Card
      ref={setNodeRef}
      paddingLevel={1}
      className={classNames('mcq__options-grid flex flex-col items-center gap-x1 object-contain', {
        'border-selected background-subtle': isOver || isActiveTarget
      })}
    >
      {valueRenderer}
      <div className='min-h-10 flex items-center self-stretch p-x1'>
        <div
          onClick={handleSlotClick}
          onKeyDown={handleKeyDown}
          role='button'
          tabIndex={0}
          aria-label={placedItem ? `Slot with ${placedItem.value}` : 'Empty slot'}
          className={cx(
            'flex min-h-x8 w-full cursor-pointer items-center justify-center rounded-lg border-dotted border-content-brand p-x2',
            {
              'background-brand-subtle-hover': isOver || isActiveTarget,
              'border-solid border-primary': placedItem && !isActiveTarget
            }
          )}
        >
          {placedItem && (
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role='presentation'
            >
              <MovableItem
                id={placedItem.id}
                value={placedItem.value}
                isActive={activeItemId === placedItem.id}
                isDragging={isDragging}
                onClick={(e) => {
                  onItemClick(placedItem.id, e);
                }}
              />
            </div>
          )}
          {(isOver || isActiveTarget) && !placedItem && <Text color='brand'>Place here</Text>}
        </div>
      </div>
    </Card>
  );
};
